import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, FileSignature } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { IMPORTANCE_BY_ID, ATTORNEY_LEVELS } from "@/data/agreements/stages";
import CategoryIcon from "./CategoryIcon";

const ATTORNEY_BY_ID = Object.fromEntries(ATTORNEY_LEVELS.map((a) => [a.id, a]));

/**
 * Plan levels from lib/agreements/recommend.js. Shared with ChecklistRow.
 * Deliberately a solid indigo ramp, so a plan level never reads as an importance badge —
 * they are different axes and both can appear on the same card.
 */
export const LEVEL_STYLES = {
  required: { label: "Required", pill: "border-transparent bg-indigo-600 text-white" },
  recommended: { label: "Recommended", pill: "border-transparent bg-indigo-100 text-indigo-800" },
  situational: { label: "Situational", pill: "border-transparent bg-slate-100 text-slate-700" },
};

const IMPORTANCE_PILL = {
  essential: "border-rose-200 bg-rose-50 text-rose-700",
  common: "border-blue-200 bg-blue-50 text-blue-700",
  situational: "border-slate-200 bg-slate-50 text-slate-700",
  advanced: "border-violet-200 bg-violet-50 text-violet-700",
};

const ATTORNEY_SHORT = {
  yes: { label: "Attorney review", pill: "border-pink-200 bg-pink-50 text-pink-800", icon: "text-pink-600" },
  "strongly-recommended": { label: "Attorney-led", pill: "border-red-200 bg-red-50 text-red-700", icon: "text-red-600" },
};

/** The level pill used on plan and checklist surfaces. Unknown levels render nothing. */
export function LevelPill({ level, className }) {
  const style = LEVEL_STYLES[level];
  if (!style) return null;
  return (
    <Badge variant="outline" className={cn("shrink-0 font-medium", style.pill, className)}>
      {style.label}
    </Badge>
  );
}

/**
 * DocumentCard — one library entry, linking to its detail page.
 *
 * props
 *   doc      a document entry from data/agreements/documents/*
 *   level    optional plan level ("required" | "recommended" | "situational") — plan context only
 *   reasons  optional string[] from the recommendation engine; the first one is shown
 *   compact  render a single dense row instead of a card
 *   to       optional link override — defaults to the document detail page.
 *            Templates page passes `/agreements/template/${doc.id}` so cards
 *            open the template directly instead of the intermediate detail.
 */
export default function DocumentCard({ doc, level, reasons, compact = false, className, to }) {
  if (!doc || !doc.id) return null;

  const href = to || `/agreements/library/${doc.id}`;
  const importance = IMPORTANCE_BY_ID[doc.importance];
  const importancePill = IMPORTANCE_PILL[doc.importance];
  const attorneyLevel = ATTORNEY_BY_ID[doc.attorneyReview] ? doc.attorneyReview : null;
  const attorney = attorneyLevel && attorneyLevel !== "depends" ? ATTORNEY_SHORT[attorneyLevel] : null;
  const firstReason = Array.isArray(reasons) ? reasons.find((r) => typeof r === "string" && r.trim()) : null;

  if (compact) {
    return (
      <Link
        to={href}
        className={cn(
          "group flex items-center gap-3 rounded-lg border p-2.5 transition-colors hover:border-foreground/30 hover:bg-muted/40",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
      >
        <CategoryIcon categoryId={doc.category} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-medium">{doc.name}</span>
            <LevelPill level={level} className="text-[10px]" />
            {doc.templateId && (
              <Badge variant="outline" className="shrink-0 gap-1 text-[10px] font-normal text-muted-foreground">
                <FileSignature className="h-3 w-3" /> Template
              </Badge>
            )}
          </div>
          <p className="truncate text-xs text-muted-foreground">
            {doc.parties || doc.subcategory || ""}
            {firstReason ? <span className="ml-1">· Because: {firstReason}</span> : null}
          </p>
        </div>
        {attorney && <AlertTriangle className={cn("hidden h-4 w-4 shrink-0 sm:block", attorney.icon)} aria-label={attorney.label} />}
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className={cn("group block h-full rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)}
    >
      <Card className="h-full transition-colors group-hover:border-foreground/30">
        <CardContent className="flex h-full flex-col gap-2.5 p-4">
          <div className="flex items-start gap-3">
            <CategoryIcon categoryId={doc.category} size="md" />
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold leading-snug">{doc.name}</h3>
              {doc.subcategory && <p className="mt-0.5 text-xs text-muted-foreground">{doc.subcategory}</p>}
            </div>
          </div>

          {doc.parties && (
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Parties: </span>
              {doc.parties}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            <LevelPill level={level} />
            {importance && (
              <Badge variant="outline" className={cn("font-medium", importancePill)}>
                {importance.name}
              </Badge>
            )}
            {attorney && (
              <Badge variant="outline" className={cn("gap-1 font-medium", attorney.pill)}>
                <AlertTriangle className="h-3 w-3" /> {attorney.label}
              </Badge>
            )}
            {doc.templateId && (
              <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
                <FileSignature className="h-3 w-3" /> Template
              </Badge>
            )}
          </div>

          {firstReason && (
            <p className="rounded-md bg-muted/60 px-2.5 py-1.5 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Because: </span>
              {firstReason}
            </p>
          )}

          <div className="mt-auto flex items-center justify-end pt-1 text-xs font-medium text-foreground">
            Open <ChevronRight className="ml-0.5 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
